import { Icon } from '@iconify/react';
import classNames from 'classnames';
import type { LinkProps } from 'next/link';
import Link from 'next/link';
import type { FC, ReactNode } from 'react';

import { useIsDropdownLinkActive } from '@/hooks/useIsDropdownLinkActive';
import { cn } from '@/lib/utils';

export type DropdownLink = { label: string; style?: 'app' } & (
  | { linkType: 'internal'; href: LinkProps['href'] }
  | { linkType: 'external'; href: string }
  | { linkType: 'none'; href?: never }
);

export type DropdownColumnLink =
  | { linkType: 'internal'; href: LinkProps['href'] }
  | { linkType: 'external'; href: string };

export type DropdownColumn = {
  title: string;
  description?: string;
  link?: DropdownColumnLink;
  links: DropdownLink[];
};

type Props = {
  label: string;
  columns: DropdownColumn[];
  theme?: 'dark' | 'light';
};

export const LinkWrapper: FC<{
  link: DropdownLink;
  className?: string;
  children: ReactNode;
}> = ({ link, className, children }) => {
  if (link.linkType === 'none') {
    return <span className={className}>{children}</span>;
  }
  if (link.linkType === 'external') {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {children}
      </a>
    );
  }
  return (
    <Link href={link.href} className={className}>
      {children}
    </Link>
  );
};

export const DropdownLinkItem: FC<{
  link: DropdownLink;
  variant?: 'dark' | 'light';
  className?: string;
}> = ({ link, variant = 'dark', className }) => {
  const isClickable = link.linkType !== 'none';
  const isActive = useIsDropdownLinkActive(link);

  if (link.style === 'app') {
    const appColor = classNames(
      'transition-colors group-hover:text-jb-primary-400',
      {
        'text-jb-primary-300': variant === 'light',
        'text-jb-primary-500': variant !== 'light',
      }
    );
    return (
      <LinkWrapper link={link} className="group flex items-center gap-1 py-1">
        <span
          className={cn(
            'whitespace-nowrap border-b border-current lg:text-base !text-jb-h5 font-semibold leading-[1.2]',
            appColor,
            className
          )}
        >
          {link.label}
        </span>
        <Icon
          icon="material-symbols:arrow-back"
          aria-hidden="true"
          className={classNames('rotate-[135deg]', appColor)}
          fontSize={20}
        />
      </LinkWrapper>
    );
  }

  const isDimmed = !isActive && !isClickable;
  const textColor = cn(
    'whitespace-nowrap py-1 lg:text-base !text-jb-h5 leading-[1.2]',
    {
      'font-semibold text-jb-primary-400': isActive,
      'transition-colors hover:text-jb-primary-400': !isActive && isClickable,
      'cursor-default': isDimmed,
      'text-white/80': !isActive && isClickable && variant === 'light',
      'text-white/50': isDimmed && variant === 'light',
      'text-jb-neutral-1000': !isActive && variant !== 'light',
    },
    className
  );

  return (
    <LinkWrapper link={link} className={textColor}>
      {link.label}
    </LinkWrapper>
  );
};

const NavDropdown: FC<Props> = ({ label, columns, theme = 'dark' }) => {
  const linkVariant = theme === 'light' ? 'dark' : 'light';

  return (
    <div
      className={classNames('flex w-max gap-2 rounded-[20px] p-6', {
        'bg-white drop-shadow-[3px_3px_25px_rgba(136,135,135,0.41)]':
          theme === 'light',
        'bg-black/30 shadow-[0_1px_10px_0_rgba(199,199,0,0.25)] backdrop-blur-xl':
          theme === 'dark',
      })}
    >
      <div className="flex h-[35px] shrink-0 items-end px-4">
        <span className="whitespace-nowrap text-base font-semibold leading-[1.2] text-jb-primary-400">
          {label}
        </span>
      </div>

      {columns.map(({ title, description, links, link: titleLink }) => (
        <div
          key={title}
          className="flex shrink-0 flex-col gap-2 self-stretch border-l border-jb-neutral-800 px-6 py-4"
        >
          <div className="flex flex-col gap-1">
            {titleLink ? (
              <LinkWrapper
                link={{ label: title, ...titleLink }}
                className="group w-fit"
              >
                <span className="whitespace-nowrap text-base font-semibold leading-[1.2] text-jb-primary-400 group-hover:underline">
                  {title}
                </span>
              </LinkWrapper>
            ) : (
              <span className="whitespace-nowrap text-base font-semibold leading-[1.2] text-jb-primary-400">
                {title}
              </span>
            )}
            {description && (
              <span
                className={classNames(
                  'whitespace-nowrap text-xs leading-[1.3]',
                  {
                    'text-jb-neutral-700': theme === 'light',
                    'text-white/60': theme === 'dark',
                  }
                )}
              >
                {description}
              </span>
            )}
          </div>

          <div className="flex flex-col">
            {links.map((link) => (
              <DropdownLinkItem
                key={link.label}
                link={link}
                variant={linkVariant}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default NavDropdown;
